import { get } from 'lodash';
import moment from 'moment';
import { i18n } from 'fishx';
import { delay } from 'redux-saga';
import cloneDeep from 'lodash/cloneDeep';
import { message } from '@whalecloud/fdx';
import { updateAccount,
  qryPaymentType,
  qryPaymentMethod,
  qryBankAcctType,
  qryBankList,
  updateCust,
  resetWscPwd,
  sendWscPwdResetOtp, modCustAddressPOA } from '@/pages/ModifyForService/services';
import { getRequestData, POI_EFF_DATE_FORMAT, poiChange, poaChange, custChange } from '@/utils';

const defaultState = {
  step: 0, // 修改客户信息业务流程的步骤
  searchCustInfo: {}, // 从搜索页面同步过来的用户信息
  serviceType: {}, // 从搜索页面同步过来的服务类型
  subServiceType: {}, // 从搜索页面同步过来的子服务类型
  customerInfo: {},
  modidyType: '', // 修改客账户信息的类型，值有modifyAccount、modifyCustomer
  paymentTypeDtoList: [],
  paymentMethodDtoList: [],
  bankAcctTypeDtoList: [],
  updateAccountParams: {},
  custAddrList: [],
  poiDefaultFileList: [],
  poaDefaultFileList: [],
  custValueMapCopy: {}, // custValueMapCopy/custAddressCopy/custCertValueCopy备份，用来比较客户、客户地址、客户证件信息是否发生变化
  custAddressCopy: {},
  custCertValueCopy: {},
  expiryTime: 120,
  timing: false,
  sendTime: 0,
};

export default {
  namespace: 'modifyForService',
  state: cloneDeep(defaultState),
  effects: {
    * updateAccount(_, { call, select }) {
      const {
        updateBillingAddressParams,
        updateBillingFrequencyParams,
        updateBillingDeliveryTypeParams,
        updateBillStatementTypeParams,
        updateMethodOfPaymentParams,
        acctDetail,
      } = yield select(state => ({
        updateBillingAddressParams: state.modifyForService.updateBillingAddressParams,
        updateBillingFrequencyParams: state.modifyForService.updateBillingFrequencyParams,
        updateBillingDeliveryTypeParams: state.modifyForService.updateBillingDeliveryTypeParams,
        updateBillStatementTypeParams: state.modifyForService.updateBillStatementTypeParams,
        updateMethodOfPaymentParams: state.modifyForService.updateMethodOfPaymentParams,
        acctDetail: state.modifyForService.acctDetail,
      }));
      let canNext = false; // 用来表示当前操作没有问题，可以继续下一个操作，每次请求发出前将其置为false，请求成功后再将其置为true
      // 因为银行账号后台返回的时候中间几位做了隐藏，所以，这里做一下比较，有变化再更新账号
      if (get(acctDetail, 'paymentProfileDto.bankAcctNbr') === get(updateMethodOfPaymentParams, 'paymentProfileDto.bankAcctNbr')) {
        delete updateMethodOfPaymentParams.paymentProfileDto.bankAcctNbr;
      }
      // 支付类型和支付方式不太容易判断是否有改动，所以每次到这里就直接更新一次支付类型和支付方式
      const updateMethodOfPaymentData = yield call(updateAccount, updateMethodOfPaymentParams);
      const updateMethodOfPaymentResp = getRequestData(updateMethodOfPaymentData);
      let resultResp = {};
      if (updateMethodOfPaymentResp.custOrderNbr) {
        canNext = true;
      }
      resultResp = updateMethodOfPaymentResp;

      // 对于接下来的操作，都是要先判断是否有改动，有改动再做修改
      // 地址有改动，更新地址
      if (get(acctDetail, 'billingAddressDto.custAddressId') !== get(updateBillingAddressParams, 'billingAddressId') && canNext) {
        canNext = false;
        const updateBillingAddrData = yield call(updateAccount, updateBillingAddressParams);
        const updateBillingAddrResp = getRequestData(updateBillingAddrData);
        if (updateBillingAddrResp.custOrderNbr) {
          canNext = true;
        }
        resultResp = updateBillingAddrResp;
      }
      // 账期有改动，修改账期
      if (get(acctDetail, 'billingCycleTypeId') !== get(updateBillingFrequencyParams, 'billingCycleType') && canNext) {
        canNext = false;
        const updateBillingFreqData = yield call(updateAccount, updateBillingFrequencyParams);
        const updateBillingFreqResp = getRequestData(updateBillingFreqData);
        if (updateBillingFreqResp.custOrderNbr) {
          canNext = true;
        }
        resultResp = updateBillingFreqResp;
      }

      // 账单介质有改动，修改账单介质
      if (get(acctDetail, 'billDeliveryTypeDtoList[0].deliveryMethod') !== get(updateBillingDeliveryTypeParams, 'billDeliveryTypeDtoList[0].deliveryMethod') && canNext) {
        canNext = false;
        const updateBillingDeliveryData = yield call(updateAccount, updateBillingDeliveryTypeParams);
        const updateBillingDeliveryResp = getRequestData(updateBillingDeliveryData);
        if (updateBillingDeliveryResp.custOrderNbr) {
          canNext = true;
        }
        resultResp = updateBillingDeliveryResp;
      }

      // 账单类型有改动，修改账单类型
      if (get(acctDetail, 'billType') !== get(updateBillStatementTypeParams, 'billType') && canNext) {
        canNext = false;
        const updateBillingStatementData = yield call(updateAccount, updateBillStatementTypeParams);
        const updateBillingStatementResp = getRequestData(updateBillingStatementData);
        if (updateBillingStatementResp.custOrderNbr) {
          canNext = true;
        }
        resultResp = updateBillingStatementResp;
      }

      return resultResp;
    },
    * updateCustomer(_, { call, select }) {
      const { updateCustParams, updateAddrParams, resetWscPwdParams, custValueMapCopy, custCertValueCopy, custAddressCopy } = yield select(state => ({
        updateCustParams: state.modifyForService.updateCustParams,
        updateAddrParams: state.modifyForService.updateAddrParams,
        resetWscPwdParams: state.modifyForService.resetWscParams,
        custValueMapCopy: state.modifyForService.custValueMapCopy,
        custCertValueCopy: state.modifyForService.custCertValueCopy,
        custAddressCopy: state.modifyForService.custAddressCopy,
      }));


      // 证件信息和客户信息不能同时做修改
      let updateCustData;
      let updateCustResp;
      let updateAddrData;
      let resetWscPwdData = {};
      let updateAddrResp;
      let updatePoiData;
      let updatePoiResp;
      let canNext = true;
      const { poiDto, custId } = updateCustParams;

      delete updateCustParams.poiDto;

      // 更新客户信息 first name/middle name/last name/BSNL_CUSTOMER_CLASSIFICATION(VIP flag)/BSNL_DO_NOT_CALL_REGISTRY(Do Not Call Registry)
      if (canNext && custChange(updateCustParams, custValueMapCopy)) {
        updateCustData = yield call(updateCust, updateCustParams);
        updateCustResp = getRequestData(updateCustData, false);
        if (updateCustResp.custOrderNbr) {
          canNext = true;
        }
      }

      // 证件类型/证件号码/生效日期/失效日期/签发机构/附件
      if (canNext && poiChange(poiDto, custCertValueCopy)) {
        // 更新证件信息
        if (canNext) {
          canNext = false;
          updatePoiData = yield call(updateCust, { poiDto, custId });
          updatePoiResp = getRequestData(updatePoiData);
          if (updatePoiResp.custOrderNbr) {
            canNext = true;
          }
        }
      }
      // 更新地址
      // custAddressCopy
      if (canNext && poaChange(get(updateAddrParams, 'addressInfo.addrExtMap.poaDto'), get(custAddressCopy, 'addrExtMap.poaDto'))) {
        canNext = false;
        updateAddrData = yield call(modCustAddressPOA, { poaDto: get(updateAddrParams, 'addressInfo.addrExtMap.poaDto'), custAddressId: get(updateAddrParams, 'addressInfo.custAddressId') });
        updateAddrResp = getRequestData(updateAddrData);
        if (get(updateAddrResp, 'custOrderNbr')) {
          canNext = true;
        }
      }


      if (canNext && resetWscPwdParams.account && resetWscPwdParams.password) {
        resetWscPwdData = yield call(resetWscPwd, resetWscPwdParams);
        const resetWscPwdResp = getRequestData(resetWscPwdData);
        if (get(resetWscPwdResp, 'returnCode') === '0') {
          canNext = false;
        }
      }

      return canNext ? { returnCode: '0', custOrderNbr: 'test' } : false;
    },
    * sendVerifyCode({ payload }, { call, put, select }) {
      const { sendTime, expiryTime } = yield select(state => ({
        sendTime: state.modifyForService.sendTime,
        expiryTime: state.modifyForService.expiryTime,
      }));

      if (Date.now() - sendTime < expiryTime * 1000) {
        message.warn(i18n('BUSINESS_TOO_OFTEN'));
        return false;
      }
      const response = yield call(sendWscPwdResetOtp, payload);
      const { returnCode, returnMsg } = getRequestData(response);
      if (returnCode === '0') {
        yield put({
          type: 'timing',
        });
        yield put({
          type: 'save',
          payload: { sendTime: Date.now() },
        });
      } else {
        message.error(returnMsg);
      }
      return false;
    },
    * timing(_, { put, select, cancel }) { // 验证码的倒计时
      let times = yield select(state => state.modifyForService.expiryTime);

      yield put({
        type: 'save',
        payload: { timing: true, times },
      });
      for (; times >= 0; times -= 1) {
        const timing = yield select(state => state.modifyForService.timing);
        if (!timing) { // 如果循环还未完成，timing就被置为false了，说明当前循环需要跳出,同时取消当前的这个任务
          yield cancel();
          break;
        }
        yield put({
          type: 'save',
          payload: { times },
        });
        yield delay(1000);
      }
      yield put({
        type: 'save',
        payload: { timing: false, times, resend: true },
      });
    },
    * qryPaymentType({ payload }, { call, put }) {
      const resp = yield call(qryPaymentType, payload);
      const { paymentTypeDtoList } = getRequestData(resp);
      yield put({
        type: 'save',
        payload: { paymentTypeDtoList: paymentTypeDtoList || [] },
      });
    },
    * qryPaymentMethod({ payload }, { call, put }) {
      const resp = yield call(qryPaymentMethod, payload);
      const { paymentMethodDtoList } = getRequestData(resp);
      yield put({
        type: 'save',
        payload: { paymentMethodDtoList: (paymentMethodDtoList || []).map(paymentMethod => {
          paymentMethod.paymentMethodId = String(paymentMethod.paymentMethodId);
          return paymentMethod;
        }),
        },
      });
    },
    * qryBankAcctType({ payload }, { call, put }) {
      const resp = yield call(qryBankAcctType, payload);
      const { bankAcctTypeDtoList } = getRequestData(resp);
      yield put({
        type: 'save',
        payload: { bankAcctTypeDtoList: (bankAcctTypeDtoList || []).map(bankAcctType => {
          bankAcctType.accountType = String(bankAcctType.accountType);
          return bankAcctType;
        }) },
      });
    },
    * qryBankList({ payload }, { call, put }) {
      const resp = yield call(qryBankList, payload);
      const { bankDtoList } = getRequestData(resp);
      yield put({
        type: 'save',
        payload: { bankDtoList: bankDtoList || [] },
      });
    },
    * handleParams({ payload }, { put, select }) {
      yield put({ type: 'save', payload });
      const { updateCustParams, updateAddrParams } = payload;
      const { custValueMap, custCertValue, custAddrList } = yield select(state => ({
        custValueMap: state.modifyForService.custValueMap,
        custCertValue: state.modifyForService.custCertValue,
        custAddrList: state.modifyForService.custAddrList,
      }));
      const { firstName, middleName, lastName, poiDto } = updateCustParams;
      const { certNbr, certType, issueOrg, effDate, expDate, effDateForReview, expDateForReview } = poiDto;
      let custAddressCopy = {};
      for (let i = 0; i < custAddrList.length; i += 1) {
        if (custAddrList[i].custAddressId === updateAddrParams.addressInfo.custAddressId) {
          custAddressCopy = custAddrList[i];
          custAddrList[i] = updateAddrParams.addressInfo;
          break;
        }
      }
      const attrValues = {};
      (updateCustParams.custAttrDtoList || []).forEach(attr => {
        if (get(attr, 'attrCode')) {
          attrValues[get(attr, 'attrCode')] = attr;
        }
      });
      // TODO 处理defaultFileList

      yield put({
        type: 'save',
        payload: {
          custValueMapCopy: custValueMap,
          custCertValueCopy: custCertValue,
          custAddressCopy,
          custValueMap: { ...custValueMap, firstName, middleName, lastName, ...attrValues },
          custCertValue: {
            ...custCertValue,
            certNbr,
            certTypeId: certType,
            issueOrg,
            effDate,
            expDate,
            effDateForReview,
            expDateForReview,
            poiEffDate: moment(effDate, POI_EFF_DATE_FORMAT),
            poiExpDate: moment(expDate, POI_EFF_DATE_FORMAT),
          },
          custAddrList: custAddrList.concat([]),
          poaDefaultFileList: custAddrList.map(addr => {
            const attachDtoList = get(addr, 'addrExtMap.poaDto.attachDtoList') || [];
            return attachDtoList.map(attach => ({
              status: 'done',
              uid: get(attach, 'attachUrl'),
              name: get(attach, 'attachName'),
              attachUrl: get(attach, 'attachUrl'),
              custAddressId: get(addr, 'custAddressId'),
            }));
          }),
          poiDefaultFileList: get(poiDto, 'attachDtoList'),
        },
      });
    },
  },

  reducers: {
    save(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
    clear() {
      return cloneDeep(defaultState);
    },
  },
};
